/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Logger } from '@app/core';
import { HttpParams } from '@angular/common/http';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { parseNumber } from '@progress/kendo-angular-intl';

export enum ApiQuerySortTypes {
  'ASC' = '',
  'DESC' = '-',
}

/**
 * Geo fence filter
 */
export class GeoFenceFilter {
  lat: number;
  long: number;
  inner_radius: number;
  outer_radius: number;
}

export class ApiQuery {
  private _log: Logger = new Logger('ApiQuery');

  private _filters: {} = {};
  private _includes: Array<string> = [];
  private _sortField: string;
  private _sortType: ApiQuerySortTypes = ApiQuerySortTypes.DESC;
  private _page = 1;
  private _limit = 15;
  private _dataSet: DataStateChangeEvent;
  private _lastKeyword: string = null;

  /**
   * Add where filters to query
   * @param key Filter field
   * @param values Searched values
   */
  addFilter(key: string, ...values: Array<string | number | boolean | {}>): this {
    values = values.map((value) => {
      if (value instanceof Object) {
        return JSON.stringify(value);
      }
      return value;
    });

    this._filters[key] = values;
    this._log.debug('addFilter -> ' + key, values);
    return this;
  }

  /**
   * Build get options
   */
  public buildGetOptions(): { params: HttpParams } {
    let httpParams: HttpParams = new HttpParams();

    /*
     * Set filters
     */
    if (Object.keys(this._filters).length > 0) {
      Object.keys(this._filters).forEach((key: string) => {
        httpParams = httpParams.set('filter[' + key + ']', encodeURIComponent(this._filters[key].join()));
      });
    }

    /*
     * Set includes
     */
    if (this._includes.length > 0) {
      httpParams = httpParams.set('include', this._includes.join());
    }

    /*
     * Set sorting
     */
    if (this._sortField) {
      httpParams = httpParams.set('sort', this._sortType + this._sortField);
    }

    /*
     * DataSet filters
     */
    if (this._dataSet) {
      Object.keys(this._dataSet).forEach((key: string) => {
        // Изчиства празните масиви
        if (this._dataSet[key] instanceof Array && this._dataSet[key].length < 1) {
          return;
        }

        // изчистване на празни данни
        if (this._dataSet[key] == null) {
          return;
        }

        // сетва лимита на страница
        if (key === 'take') {
          this.setLimit(parseNumber(this._dataSet[key]));
          return;
        }

        // сетва страницата
        if (key === 'skip') {
          if (this._dataSet[key] > 0) {
            this.setPage(parseNumber(this._dataSet[key] / this._dataSet['take'] + 1));
          } else {
            this.setPage(1);
          }
          return;
        }

        // httpParams = this.setRecursiveHttpParams(httpParams, key, this._dataSet[key]);
        // console.log(encodeURIComponent(JSON.stringify(this._dataSet[key]));

        if (key === 'filter') {
          key = 'dataset_filters';
          this._dataSet[key] = JSON.parse(JSON.stringify(this._dataSet.filter));
          this._dataSet[key].filters.forEach((filter: any) => {
            filter.value = encodeURIComponent(filter.value);
          });
        }

        if (key === 'sort') {
          key = 'dataset_sort';
          this._dataSet[key] = this._dataSet['sort'];
        }

        httpParams = httpParams.set(key, JSON.stringify(this._dataSet[key]));
      });
    }

    /*
     * Set page
     */
    if (this._page > 1) {
      httpParams = httpParams.set('page', JSON.stringify(this._page));
    }

    /*
     * Set limit
     */
    if (this._limit > 0) {
      httpParams = httpParams.append('limit', JSON.stringify(this._limit));
    }

    return { params: httpParams };
  }

  /**
   * Add relation include to query
   * @param include Relation to include
   */
  addInclude(include: string): this {
    if (this._includes.indexOf(include) <= -1) {
      this._includes.push(include);
    }
    return this;
  }

  /**
   * Add relation includes to query
   * @param includes Relations to include
   */
  addIncludes(...includes: Array<string>): this {
    includes.forEach((item) => {
      this.addInclude(item);
    });
    return this;
  }

  /**
   * Set geo fence filter to query
   * @param geoFenceFilter GeoFence object
   * @param field Field name OPTIONAL
   */
  setGeoFence(geoFenceFilter: GeoFenceFilter, field: string = 'geofence'): this {
    return this.addFilter(field, geoFenceFilter);
  }

  /**
   * Remove geo fence filter from query
   * @param field Field name OPTIONAL
   */
  removeGeoFence(field: string = 'geofence'): this {
    this.removeFilter(field);
    return this;
  }

  /**
   * Set sorting
   * @param field Field name
   * @param type Sorting order
   */
  setSort(field: string, type: ApiQuerySortTypes = ApiQuerySortTypes.DESC): this {
    this._sortField = field;
    this._sortType = type;
    return this;
  }

  /**
   * Set pagination limit
   * @param limit Per page limit
   */
  setLimit(limit: number): this {
    this._limit = limit;
    this._log.debug('setLimit', this._limit);
    return this;
  }

  /**
   * Set page
   * @param page Page number
   */
  setPage(page: number | { offset: number }): this {
    if (typeof page === 'number') {
      this._page = page;
    } else if (page instanceof Object && page.offset >= 0) {
      /*
      Data table parameters parse
       */
      this._page = page.offset + 1;
    } else {
      throw new Error('setPage `page` parameter error!');
    }

    this._log.debug('setPage', this._page);
    return this;
  }

  /**
   * Add where filters to get query
   * @param filters Filters object
   */
  addFilters(filters: {}): this {
    const $this = this;

    Object.keys(filters).forEach((key: string) => {
      if (filters[key] instanceof Array) {
        $this._filters[key] = filters[key];
      } else {
        $this._filters[key] = [filters[key]];
      }
    });

    this._log.debug('addFilters -> ', filters);
    return this;
  }

  /**
   * check if filter is set
   * @param filters Filters object
   */
  hasFilter(key: string): boolean {
    return this._filters[key] != null;
  }

  /**
   * remove where from filters to query
   * @param key Filter field
   */
  removeFilter(key: string): this {
    delete this._filters[key];
    this._log.debug('removeFilter -> ' + key);
    return this;
  }

  /**
   * Add where filters to get query
   * @param filters Filters object
   */
  removeFilters(filters: string[]): this {
    const $this = this;

    filters.forEach((key: string) => {
      $this.removeFilter(key);
    });

    this._log.debug('removeFilters -> ', filters);
    return this;
  }

  /**
   *
   * @param dataSet DataSetChangeEvent
   */
  setDataSetFilters(dataSet: DataStateChangeEvent): this {
    this._log.debug(dataSet);
    this._dataSet = JSON.parse(JSON.stringify(dataSet));
    return this;
  }

  getDataSet() {
    return this._dataSet;
  }
  public handleKeyword(keyword: string): this {
  // Trim keyword for comparison
  const cleanKeyword = (keyword || '').trim();

  // If keyword changed, reset page
  if (cleanKeyword !== this._lastKeyword) {
    this._page = 1;
    this._lastKeyword = cleanKeyword;
  }

  return this;
}

}
